/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { IconButton, Spinner, SpinnerSize, Stack, StackItem, Text } from "@fluentui/react";

import { SidePanel } from "Components/SidePanel/SidePanel";

import { IUpdateSAProgramStatusSidePanelProps } from "./IUpdateSAProgramStatusSidePanelProps";
import { ISubmitCSVFileRequest, IUploadCSVFileRequest } from "Interfaces/SA/IUploadCSVFileRequest";
import { IUploadCsvFileValidation } from "Interfaces/SA/IUploadCSVFileRequest";

import { constructCsvDownload } from "Utility/DownloadHelper";
import { convertCsvToBase64, validateFileUpload } from "Utility/FileUploadHelper";

import {
  submitCsvFile,
  uploadCsvFile,
  uploadCsvFileReset,
} from "Dataflows/SA/UpdateSAProgramStatus/UpdateSAProgramStatusAction";
import {
  getCsvUploadState,
  getCsvUploadErrors,
  getCsvSubmitStatus,
} from "Dataflows/SA/UpdateSAProgramStatus/UpdateSAProgramStatusSelector";
import { MLSA_PROGRAM_STATUS_TEMPLATE } from "Constants/ProfileConstants";
import {
  errorText,
  fileInputs,
  fileUploadContainer,
  iconButton,
  summaryText,
} from "Components/UpdateSARegistrationStatus/UpdateSARegistrationStatus.styles";

/**
 * Update Student Ambassador Program component.
 * @param {IUpdateSAProgramStatusSidePanelProps} props the component props.
 * @returns {ReactElement} the component element.
 */
export const UpdateSAProgramStatusSidePanel = (
  props: IUpdateSAProgramStatusSidePanelProps
): ReactElement => {
  const dispatch = useDispatch();

  const { isPanelOpen, setIsPanelOpen } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileUploadError, setFileUploadError] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const isUploading = useSelector(getCsvUploadState);
  const csvError: IUploadCsvFileValidation = useSelector(getCsvUploadErrors);
  const isSubmitted = useSelector(getCsvSubmitStatus);

  /**
   * reset State function.
   */
  const resetState = (): void => {
    dispatch(uploadCsvFileReset());
  };

  /**
   * dismiss Panel function.
   */
  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    resetState();
  };

  useEffect(() => dismissPanel, [isSubmitted]);

  /**
   * On click of submit.
   */
  const onClickOfSubmit = () => {
    const request: ISubmitCSVFileRequest = { fileId: csvError.fileId };
    dispatch(submitCsvFile(request));
  };

  /**
   * handle Download Static CSV.
   */
  const handleDownloadStaticCSV = (): void => {
    constructCsvDownload(MLSA_PROGRAM_STATUS_TEMPLATE, "MLSA_Program_Status_Template.csv");
  };

  /**
   * handle File upload Change.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileUploadError("");
    resetState();
    const errorMessage = validateFileUpload(event);
    if (errorMessage) {
      setFileUploadError(errorMessage);
    } else {
      const file = event.target.files;
      if (file && file.length > 0) {
        setSelectedFileName(file[0].name);
        convertCsvToBase64(file[0])
          .then((base64String) => {
            const request: IUploadCSVFileRequest = { CsvBase64: base64String };
            dispatch(uploadCsvFile(request));
          })
          .catch((error) => {
            setFileUploadError("Error converting file");
          });
      }
    }
  };

  return (
    <form id="sdf">
      <SidePanel
        isOpen={isPanelOpen}
        title="Update Program Status"
        saveButtonLabel="Submit"
        disableActions={!csvError.isValid}
        onDismiss={dismissPanel}
        onSave={() => onClickOfSubmit()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem align="start">
            <Text>
              Follow the steps below to update Student Ambassadors Program status.{" "}
              <strong>Please note this action DOES NOT trigger transactional email..</strong>
            </Text>
          </StackItem>
          <StackItem>
            <Text>1. Download CSV template (optional)</Text>
            <StackItem>
              <Button onClick={handleDownloadStaticCSV}>Download</Button>
            </StackItem>
          </StackItem>
          <StackItem>
            <Text>2. Edit your CSV file</Text>
          </StackItem>
          <StackItem>
            <Text>3. Upload your CSV file</Text>
            <br />
            <div style={fileUploadContainer}>
              <input
                type="text"
                placeholder="Select a file"
                style={fileInputs}
                value={selectedFileName}
                readOnly
              />
              <IconButton
                disabled={isUploading}
                styles={iconButton}
                iconProps={{ iconName: "FabricFolder" }}
                onClick={(): void => fileInputRef.current?.click()}
                tabIndex={0}
              ></IconButton>
              <input
                hidden
                id="base64str"
                name="base64str"
                type="file"
                accept=".csv"
                ref={fileInputRef}
                multiple={false}
                onChange={handleFileChange}
              />
              {isUploading && <Spinner size={SpinnerSize.small} />}
            </div>
            <div style={errorText}>{fileUploadError}</div>
          </StackItem>
          {csvError.summary.length > 0 && (
            <StackItem style={summaryText}>
              {csvError.summary.map((item: string, index) => {
                return <p key={"summary" + index}>{item}</p>;
              })}
            </StackItem>
          )}
          {csvError.errors.length > 0 && (
            <StackItem style={errorText}>
              {csvError.errors.map((item: string, index) => {
                return <p key={"errors" + index}>{item}</p>;
              })}
            </StackItem>
          )}
        </Stack>
      </SidePanel>
    </form>
  );
};
